/**
 * /tools/tls-inspector — public TLS/SSL inspector.
 * Mirrors the DNS Health UX (score dial + section cards + recommendations + FAQ).
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Lock, ShieldCheck, AlertTriangle, Loader2, Shield, Server,
    Globe, Key, RefreshCw, ChevronDown, ChevronUp, Award,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { postJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { ToolPageMeta } from "../components/ToolPageMeta";

const GRADE_COLORS = {
    A: "text-accent-mint",  B: "text-accent-cyan",
    C: "text-accent-amber", D: "text-accent-amber", F: "text-accent-red",
};

const ScoreDial = ({ score, grade }) => (
    <div className="relative inline-flex items-center justify-center" data-testid="tls-score-dial">
        <svg width="148" height="148" viewBox="0 0 148 148">
            <circle cx="74" cy="74" r="64" stroke="rgba(255,255,255,0.06)" strokeWidth="10" fill="none" />
            <circle cx="74" cy="74" r="64" stroke="currentColor" strokeWidth="10" fill="none"
                strokeLinecap="round" transform="rotate(-90 74 74)"
                className={GRADE_COLORS[grade] || "text-accent-cyan"}
                style={{ strokeDasharray: 2 * Math.PI * 64, strokeDashoffset: 2 * Math.PI * 64 * (1 - score / 100), transition: "stroke-dashoffset 1s ease-out" }} />
        </svg>
        <div className="absolute text-center">
            <div className={`font-display font-semibold text-4xl tabular-nums ${GRADE_COLORS[grade] || "text-text-primary"}`}>{score}</div>
            <div className={`text-[12px] font-mono uppercase tracking-[0.22em] ${GRADE_COLORS[grade] || "text-text-muted"}`}>Grade {grade}</div>
        </div>
    </div>
);

const PROTO_LABEL = {
    "TLSv1.0": { name: "TLS 1.0", legacy: true,  bad: true },
    "TLSv1.1": { name: "TLS 1.1", legacy: true,  bad: true },
    "TLSv1.2": { name: "TLS 1.2", legacy: false, bad: false },
    "TLSv1.3": { name: "TLS 1.3", legacy: false, bad: false },
};

const SectionCard = ({ icon: Icon, title, children, testId }) => (
    <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5" data-testid={testId}>
        <div className="flex items-center gap-2 mb-3">
            <Icon className="w-4 h-4 text-accent-cyan" />
            <h3 className="font-display font-semibold text-[14px]">{title}</h3>
        </div>
        {children}
    </div>
);

const FAQ = [
    {
        q: "What is TLS and why does it matter?",
        a: "TLS (Transport Layer Security) encrypts traffic between your visitors and your server. Modern TLS (1.2+ with HSTS) is required by GDPR, PCI-DSS, and the NIS2 directive. Weak TLS configurations enable man-in-the-middle attacks, credential theft, and trust-trust certificate-warning bounce-rates.",
    },
    {
        q: "How can I disable TLS 1.0 and 1.1?",
        a: "On NGINX: `ssl_protocols TLSv1.2 TLSv1.3;`. On Apache: `SSLProtocol all -SSLv3 -TLSv1 -TLSv1.1`. On IIS: use IIS Crypto. Reload the web server after the change and re-run this scan to verify.",
    },
    {
        q: "What is HSTS and should I enable it?",
        a: "HSTS (HTTP Strict Transport Security) tells browsers to refuse plain HTTP for your domain. Add `Strict-Transport-Security: max-age=31536000; includeSubDomains; preload`. Submit to https://hstspreload.org/ for browser-level enforcement.",
    },
    {
        q: "What if my certificate expires soon?",
        a: "Renew within the next 30 days. Let's Encrypt + certbot + a cron job is free and reliable. For business-critical infrastructure, consider an EV or OV cert and a monitored expiry alert.",
    },
];

export const TlsInspector = () => {
    usePageMeta({
        title: "Free TLS/SSL Inspector — Cybersecurity Forefront",
        description:
            "Free TLS/SSL inspector. Check your domain's certificate, supported TLS versions, HSTS, cipher suites, and get an A-F grade with actionable recommendations.",
        jsonLd: {
            "@context": "https://schema.org", "@type": "WebApplication",
            name: "TLS/SSL Inspector",
            applicationCategory: "SecurityApplication",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
    });

    const [host, setHost] = useState("");
    const [port, setPort] = useState(443);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [openFaq, setOpenFaq] = useState(null);

    const submit = async (e) => {
        e?.preventDefault();
        const clean = host.trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0];
        if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(clean)) {
            toast.error("Enter a valid domain like example.com");
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const res = await postJSON("/tools/tls-inspector", { host: clean, port: Number(port) });
            setResult(res);
        } catch (e) {
            const msg = e?.message || "Inspection failed";
            if (msg.toLowerCase().includes("rate limit")) {
                toast.error("Rate limit reached. Sign up free to unlock more scans.");
            } else {
                toast.error(msg);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-16" data-testid="tls-inspector-page">
            <div className="max-w-3xl mx-auto px-5">
                <ToolPageMeta toolId="tls-inspector" />
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Free Tool</p>
                <h1 className="mt-2 font-display font-semibold text-4xl sm:text-5xl tracking-tight">
                    Inspect any site's TLS posture.
                </h1>
                <p className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-xl">
                    Get the cert chain, protocol matrix, HSTS state, and an A-F grade in under 5 seconds.
                    No signup. Anonymous lookups limited to 3/24h.
                </p>

                <form onSubmit={submit} className="mt-7 flex gap-2 flex-wrap" data-testid="tls-form">
                    <input
                        type="text" value={host} onChange={(e) => setHost(e.target.value)}
                        placeholder="example.com"
                        data-testid="tls-host-input"
                        className="flex-1 min-w-[200px] px-4 py-3 bg-surface border border-white/[0.08] rounded-md text-[14px] focus:outline-none focus:border-accent-cyan" />
                    <input
                        type="number" value={port} onChange={(e) => setPort(e.target.value)} min={1} max={65535}
                        data-testid="tls-port-input"
                        className="w-24 px-3 py-3 bg-surface border border-white/[0.08] rounded-md text-[14px] focus:outline-none focus:border-accent-cyan" />
                    <button type="submit" disabled={busy} data-testid="tls-submit"
                        className="inline-flex items-center gap-1.5 px-5 py-3 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50">
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</> : <><Lock className="w-4 h-4" /> Inspect</>}
                    </button>
                </form>

                {busy && (
                    <div className="mt-6 text-[12.5px] text-text-muted font-mono">
                        <p>→ TCP connect…</p>
                        <p>→ Negotiating TLS handshake…</p>
                        <p>→ Parsing X.509 chain…</p>
                        <p>→ Probing TLS 1.0 / 1.1 / 1.2 / 1.3 in parallel…</p>
                    </div>
                )}

                {result && (
                    <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 space-y-5" data-testid="tls-results">
                        {/* Score */}
                        <div className="flex items-center gap-5 flex-wrap rounded-xl border border-white/[0.06] bg-surface/60 p-5">
                            <ScoreDial score={result.score} grade={result.grade} />
                            <div className="flex-1 min-w-[200px]">
                                <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-text-dim">Host</p>
                                <p className="font-mono text-accent-cyan text-[15px]">{result.host}:{result.port}</p>
                                <p className="mt-2 text-[12.5px] text-text-muted">{result.summary}</p>
                            </div>
                            <button onClick={submit} className="text-[11px] inline-flex items-center gap-1 text-text-muted hover:text-accent-cyan" data-testid="tls-rerun">
                                <RefreshCw className="w-3 h-3" /> Re-scan
                            </button>
                        </div>

                        {/* Certificate */}
                        <SectionCard icon={Award} title="Certificate" testId="tls-cert-section">
                            {result.sections.certificate?.subject ? (
                                <div className="text-[12.5px] space-y-1.5">
                                    <KV k="Subject"   v={result.sections.certificate.subject} />
                                    <KV k="Issuer"    v={result.sections.certificate.issuer} />
                                    <KV k="Expires"   v={`${result.sections.certificate.not_after} (${result.sections.certificate.days_to_expiry} days)`}
                                        tone={result.sections.certificate.days_to_expiry < 30 ? "red" : "mint"} />
                                    <KV k="Signature" v={result.sections.certificate.signature_algorithm} />
                                    <KV k="Key size"  v={`${result.sections.certificate.key_size_bits} bits`} />
                                    {result.sections.certificate.sans?.length > 0 && (
                                        <KV k="SANs" v={result.sections.certificate.sans.slice(0, 8).join(", ") + (result.sections.certificate.sans.length > 8 ? ` (+${result.sections.certificate.sans.length - 8} more)` : "")} />
                                    )}
                                </div>
                            ) : (
                                <p className="text-[12.5px] text-accent-red">Could not fetch certificate.</p>
                            )}
                        </SectionCard>

                        {/* Protocols */}
                        <SectionCard icon={Server} title="Supported TLS versions" testId="tls-protocols-section">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {Object.entries(result.sections.protocols).map(([k, v]) => {
                                    const meta = PROTO_LABEL[k] || { name: k };
                                    const ok = v.ok;
                                    return (
                                        <div key={k} className={["rounded-md border p-3",
                                            ok ? (meta.bad ? "border-accent-red/40 bg-accent-red/[0.05]" : "border-accent-mint/40 bg-accent-mint/[0.05]") :
                                                 "border-white/[0.06] bg-canvas/30"
                                        ].join(" ")} data-testid={`tls-proto-${k.replace(/\W/g, '').toLowerCase()}`}>
                                            <p className="text-[12.5px] font-semibold">{meta.name}</p>
                                            <p className={["mt-1 text-[10.5px] font-mono uppercase",
                                                ok ? (meta.bad ? "text-accent-red" : "text-accent-mint") : "text-text-dim"
                                            ].join(" ")}>{ok ? (meta.bad ? "Enabled (bad)" : "Enabled") : "Disabled"}</p>
                                            {v.cipher && <p className="mt-1 text-[10px] text-text-dim font-mono truncate">{v.cipher}</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        </SectionCard>

                        {/* HSTS */}
                        <SectionCard icon={ShieldCheck} title="HTTP Strict Transport Security" testId="tls-hsts-section">
                            {result.sections.hsts?.present ? (
                                <div className="space-y-1.5 text-[12.5px]">
                                    <KV k="Header"          v={result.sections.hsts.raw} />
                                    <KV k="max-age"         v={`${result.sections.hsts.max_age}s ${result.sections.hsts.max_age >= 31536000 ? "(≥ 1 year ✓)" : "(< 1 year)"}`}
                                        tone={result.sections.hsts.max_age >= 31536000 ? "mint" : "amber"} />
                                    <KV k="includeSubDomains" v={result.sections.hsts.include_subdomains ? "✓" : "—"} />
                                    <KV k="preload"           v={result.sections.hsts.preload ? "✓" : "—"} />
                                </div>
                            ) : (
                                <p className="text-[12.5px] text-accent-amber">HSTS header not detected on HTTPS root.</p>
                            )}
                        </SectionCard>

                        {/* Breakdown */}
                        {(result.sections.breakdown || []).length > 0 && (
                            <SectionCard icon={ShieldCheck} title="Score breakdown" testId="tls-breakdown">
                                <ul className="space-y-1 text-[12.5px]">
                                    {result.sections.breakdown.map((b, i) => (
                                        <li key={i} className="flex justify-between">
                                            <span className="text-text-muted">{b.label}</span>
                                            <span className="font-mono text-accent-mint">+{b.pts} pts</span>
                                        </li>
                                    ))}
                                </ul>
                            </SectionCard>
                        )}

                        {/* Recommendations */}
                        {result.recommendations.length > 0 && (
                            <div className="rounded-xl border border-accent-amber/30 bg-accent-amber/[0.05] p-5" data-testid="tls-recommendations">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-accent-amber" />
                                    <h3 className="font-display font-semibold text-[14px] text-accent-amber">Recommended actions</h3>
                                </div>
                                <ul className="space-y-1.5">
                                    {result.recommendations.map((r, i) => (
                                        <li key={i} className="text-[13px] flex items-start gap-2">
                                            <span className="text-accent-amber">•</span> <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* CTA */}
                        <div className="rounded-xl border border-accent-cyan/30 bg-accent-cyan/[0.05] p-5 flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <p className="font-display font-semibold text-[14px]">Want monitored TLS + alerts?</p>
                                <p className="mt-1 text-[12.5px] text-text-muted">Cybersecurity Forefront's managed cert + endpoint monitoring catches expirations 30 days out.</p>
                            </div>
                            <Link to="/pricing" className="px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan">See Pro plans →</Link>
                        </div>
                    </motion.section>
                )}

                {/* SEO content */}
                <section className="mt-16 prose prose-invert max-w-none">
                    <h2 className="font-display font-semibold text-2xl tracking-tight">How the TLS Inspector works</h2>
                    <p className="text-[14px] text-text-muted leading-relaxed mt-3">
                        Each scan opens four pinned TLS sockets (one per protocol version) in parallel to detect what your server actually accepts.
                        We then download the certificate chain, parse it with the Python <code>cryptography</code> library, and read the
                        <code>Strict-Transport-Security</code> header off the HTTPS root. The weighted score (cert validity 25, modern
                        protocols 30, HSTS 25, key strength 10, chain reachability 5) maps to a grade A-F, exactly the way SSL Labs
                        does — but free, instant, and without a queue.
                    </p>
                    <h3 className="mt-6 font-display font-semibold text-xl">Frequently asked questions</h3>
                    <div className="mt-3 space-y-2">
                        {FAQ.map((f, i) => (
                            <div key={i} className="rounded-md border border-white/[0.06] bg-surface/30">
                                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full flex items-center justify-between px-4 py-3 text-left">
                                    <span className="text-[13.5px] font-medium">{f.q}</span>
                                    {openFaq === i ? <ChevronUp className="w-4 h-4 text-text-dim" /> : <ChevronDown className="w-4 h-4 text-text-dim" />}
                                </button>
                                {openFaq === i && (
                                    <div className="px-4 pb-3 text-[13px] text-text-muted">{f.a}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

const KV = ({ k, v, tone }) => {
    const toneClass = tone === "red" ? "text-accent-red" : tone === "mint" ? "text-accent-mint" : tone === "amber" ? "text-accent-amber" : "text-text-primary";
    return (
        <div className="flex justify-between gap-3">
            <span className="text-text-muted">{k}</span>
            <span className={`font-mono text-right truncate ml-2 ${toneClass}`}>{v}</span>
        </div>
    );
};

export default TlsInspector;
